import type { KnowledgeSearchHit } from '@/api/knowledgeApi';
import { extractFirstHttpUrl } from '@/utils/projectCreateIssueSummaryMeta';
import { searchKnowledgeHits } from '@/utils/knowledgeSearch';

/** 随 Agent 请求传入 ai-service client_context，供提示词与结构化校验 */
export type ReferenceSearchHitPayload = {
  title: string;
  url: string;
  documentId: string;
  snippet: string;
  kbId: string;
};

export type ReferenceMaterialRow = {
  title: string;
  url: string;
  category: string;
  snippet?: string;
  /** 知识库文档 id（无外链时便于后续扩展跳转） */
  documentId?: string;
};

function hitKey(h: KnowledgeSearchHit): string {
  const id = String(h.documentId ?? h.document_id ?? '').trim();
  if (id) return `id:${id}`;
  const t = String(h.title ?? '').trim().toLowerCase();
  const s = String(h.source ?? '').trim();
  return `t:${t}|s:${s}`;
}

/** source 字段若为 http(s) 则作为可点击外链 */
export function sourceAsHttpUrl(source: unknown): string {
  const s = String(source ?? '').trim();
  if (/^https?:\/\//i.test(s)) return s;
  return '';
}

/**
 * 为「新建项目」对话预检索知识库：技术开发接口、行业标准、对标手册、客户介绍等方向。
 * 合并多路查询后去重，返回至多 8 条供模型筛选 3～5 条写入 structured_result。
 */
export async function fetchProjectCreateReferenceKnowledgeHits(userText: string): Promise<ReferenceSearchHitPayload[]> {
  const base = (userText || '').trim().slice(0, 480);
  if (!base) return [];
  const q2 = `${base} 行业标准 规范 认证 车规`;
  const q3 = `${base} 接口 API 协议 SDK 技术文档`;
  const q4 = `${base} 产品手册 对标 竞品 客户 需求`;
  try {
    const [a, b, c, d] = await Promise.all([
      searchKnowledgeHits(base, { top_k: 6 }).catch(() => [] as KnowledgeSearchHit[]),
      searchKnowledgeHits(q2, { top_k: 5 }).catch(() => [] as KnowledgeSearchHit[]),
      searchKnowledgeHits(q3, { top_k: 5 }).catch(() => [] as KnowledgeSearchHit[]),
      searchKnowledgeHits(q4, { top_k: 5 }).catch(() => [] as KnowledgeSearchHit[]),
    ]);
    const merged = new Map<string, ReferenceSearchHitPayload>();
    const scoreMap = new Map<string, number>();
    for (const batch of [a, b, c, d]) {
      for (const h of batch) {
        const key = hitKey(h);
        if (!key || key === 't:|s:') continue;
        const sc = typeof h.score === 'number' && !Number.isNaN(h.score) ? h.score : 0;
        const prev = scoreMap.get(key) ?? -1;
        if (sc >= prev) scoreMap.set(key, sc);
        if (merged.has(key)) continue;
        const title = String(h.title ?? h.document_id ?? h.documentId ?? '未命名文献').trim() || '未命名文献';
        const url = sourceAsHttpUrl(h.source);
        const documentId = String(h.documentId ?? h.document_id ?? '').trim();
        const kbId = String(h.kbId ?? h.kb_id ?? '').trim();
        const snippet = String(h.snippet ?? '')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 320);
        merged.set(key, { title, url, documentId, snippet, kbId });
      }
    }
    const sorted = [...merged.entries()].sort((x, y) => (scoreMap.get(y[0]) ?? 0) - (scoreMap.get(x[0]) ?? 0));
    return sorted.slice(0, 8).map(([, v]) => v);
  } catch {
    return [];
  }
}

function pickStr(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

/** 用预检索命中补全 referenceMaterials 的 url / snippet（按 documentId 或标题匹配） */
export function mergeReferenceRowsWithSearchHits(
  rows: ReferenceMaterialRow[],
  hits?: ReferenceSearchHitPayload[] | null,
): ReferenceMaterialRow[] {
  if (!hits?.length) return rows;
  return rows.map((r) => {
    if (pickStr(r.url)) return r;
    const did = pickStr(r.documentId);
    const titleL = r.title.trim().toLowerCase();
    let hit: ReferenceSearchHitPayload | undefined;
    if (did) {
      const dl = did.toLowerCase();
      hit = hits.find((h) => pickStr(h.documentId).toLowerCase() === dl);
    }
    if (!hit && titleL) {
      hit = hits.find((h) => pickStr(h.title).toLowerCase() === titleL);
    }
    if (!hit && titleL.length >= 4) {
      hit = hits.find((h) => {
        const ht = pickStr(h.title).toLowerCase();
        return ht.length >= 4 && (titleL.includes(ht) || ht.includes(titleL));
      });
    }
    if (!hit) return r;
    const nu = pickStr(hit.url) || r.url;
    const ns = pickStr(r.snippet) || pickStr(hit.snippet);
    const ndid = pickStr(r.documentId) || pickStr(hit.documentId);
    const nt = pickStr(r.title) || pickStr(hit.title);
    return {
      title: nt || r.title,
      url: nu,
      category: r.category,
      ...(ns ? { snippet: ns.slice(0, 400) } : {}),
      ...(ndid ? { documentId: ndid } : {}),
    };
  });
}

const REF_MATERIAL_HEADING_RE = /\*{0,2}参考材料\*{0,2}\s*[:：]?\s*/;

/**
 * 从「合规与标准」等长文本中切掉末尾「参考材料」枚举段，避免与下方「参考材料」列表重复堆叠。
 * 支持 `#### 参考材料`、`**参考材料**：` 等常见模型写法。
 */
export function splitNormsComplianceAndReferenceTail(full: string): { complianceOnly: string; referenceTail: string } {
  const raw = String(full ?? '').replace(/\r\n/g, '\n');
  if (!raw.trim()) return { complianceOnly: '', referenceTail: '' };
  const mHash = raw.search(/(?:^|\n)#{1,4}\s*参考材料\s*\n+/);
  const mBold = raw.search(new RegExp(`(?:^|\\n)${REF_MATERIAL_HEADING_RE.source}`, 'm'));
  let cut = -1;
  if (mBold >= 0) cut = mBold;
  if (mHash >= 0 && (cut < 0 || mHash < cut)) cut = mHash;
  if (cut < 0) return { complianceOnly: raw.trim(), referenceTail: '' };
  const head = raw.slice(0, cut).trim();
  const after = raw.slice(cut).replace(/^\s+/, '');
  const tail = after
    .replace(/^#{1,4}\s*参考材料\s*\n+/i, '')
    .replace(/^\*{1,2}参考材料\*{1,2}\s*[:：]?\s*(?:\n+|$)/i, '')
    .trim();
  return { complianceOnly: head, referenceTail: tail };
}

/** 从规范正文「参考材料」段解析条目标题（含 `1. xxx（暂无外链）`） */
export function extractReferenceMaterialTitlesFromNormsText(blob: string): string[] {
  const t = String(blob ?? '').replace(/\r\n/g, '\n');
  if (!t.trim()) return [];
  let body = t;
  const headIdx = body.search(new RegExp(`(?:^|\\n)${REF_MATERIAL_HEADING_RE.source}`, 'm'));
  if (headIdx >= 0) {
    body = body.slice(headIdx).replace(new RegExp(`^.*?${REF_MATERIAL_HEADING_RE.source}`, 'ms'), '');
  }
  const titles: string[] = [];
  for (const line of body.split('\n')) {
    const s = line.replace(/\u00a0/g, ' ').trim();
    if (!s || /^[-=]{3,}\s*$/.test(s)) break;
    const m = s.match(/^(?:\d+[.)]\s*|[-*]\s*)(.+)$/);
    const chunk = (m ? m[1] : s).trim();
    if (!chunk) continue;
    const title = chunk
      .replace(/\s*[（(]暂无外链[）)]\s*$/i, '')
      .replace(/\s*[:-：]\s*暂无外链.*$/i, '')
      .replace(/\s*（无链接）\s*$/i, '')
      .trim();
    if (title.length >= 2 && !/^参考材料/.test(title)) titles.push(title);
  }
  return titles;
}

/** JSON referenceMaterials + 正文参考材料列表 + 预检索命中合并 */
export function parseReferenceMaterialsWithNormsBlob(
  structured: Record<string, unknown> | null | undefined,
  normsTextParts: string[],
  searchHits?: ReferenceSearchHitPayload[] | null,
): ReferenceMaterialRow[] {
  const fromJson = parseReferenceMaterials(structured, searchHits);
  const blob = normsTextParts.map((x) => String(x ?? '').trim()).filter(Boolean).join('\n\n');
  const titles = extractReferenceMaterialTitlesFromNormsText(blob);
  const seen = new Set(fromJson.map((r) => r.title.trim().toLowerCase()));
  const extra: ReferenceMaterialRow[] = [];
  for (const title of titles) {
    const k = title.trim().toLowerCase();
    if (!k || seen.has(k)) continue;
    let dup = false;
    for (const ex of extra) {
      if (ex.title.trim().toLowerCase() === k) {
        dup = true;
        break;
      }
    }
    if (dup) continue;
    seen.add(k);
    extra.push({ title: title.trim(), url: '', category: '综合' });
  }
  return mergeReferenceRowsWithSearchHits([...fromJson, ...extra], searchHits).slice(0, 8);
}

/** 解析 structured_result.referenceMaterials；可传入预检索命中以补全空 url */
export function parseReferenceMaterials(
  structured: Record<string, unknown> | null | undefined,
  searchHits?: ReferenceSearchHitPayload[] | null,
): ReferenceMaterialRow[] {
  const raw = structured?.referenceMaterials ?? structured?.reference_materials;
  if (!Array.isArray(raw)) return [];
  const out: ReferenceMaterialRow[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const o = row as Record<string, unknown>;
    const title = pickStr(o.title ?? o.name ?? o.documentTitle);
    if (!title) continue;
    let url = pickStr(o.url ?? o.link ?? o.href);
    const category = pickStr(o.category ?? o.type ?? '综合') || '综合';
    const snippet = pickStr(o.snippet ?? o.summary);
    const documentId = pickStr(o.documentId ?? o.document_id);
    if (!url) {
      url = extractFirstHttpUrl(snippet) || extractFirstHttpUrl(title);
    }
    out.push({
      title,
      url,
      category,
      ...(snippet ? { snippet: snippet.slice(0, 400) } : {}),
      ...(documentId ? { documentId } : {}),
    });
    if (out.length >= 8) break;
  }
  return mergeReferenceRowsWithSearchHits(out, searchHits);
}
